import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router';
import { Search, Plus, ArrowUpDown, ArrowUp, ArrowDown, UserCheck, Download } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import { getAdminDistributors, createDistributor, downloadDistributorExport, AdminDistributor } from '../../services/adminApi';
import { handleImpersonate } from '../../utils/impersonate';
import SubcategoryExclusionDrawer from '../../components/SubcategoryExclusionDrawer';
import {
  getAdminSubcategoryExclusions,
  updateAdminSubcategoryExclusions,
  SubcategoryExclusionsResponse,
} from '../../services/api';
import { AdminEmptyState } from './_adminEmptyState';
import { ADMIN_PAGE_FRAME, ADMIN_PAGE_FRAME_STYLE } from '../../components/admin/adminPageFrame';

type SortField = 'name' | 'region' | 'status' | 'rep_count' | 'product_count' | 'categorization_pct' | 'created_at';
type SortDir = 'asc' | 'desc';

export function QMAdminDistributors() {
  const [distributors, setDistributors] = useState<AdminDistributor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [newRegion, setNewRegion] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminFirstName, setNewAdminFirstName] = useState('');
  const [newAdminLastName, setNewAdminLastName] = useState('');
  const [creating, setCreating] = useState(false);
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [exclusionDrawerOpen, setExclusionDrawerOpen] = useState(false);
  const [exclusionDistributorId, setExclusionDistributorId] = useState<string | null>(null);
  const [exclusionData, setExclusionData] = useState<SubcategoryExclusionsResponse | null>(null);
  const [exclusionLoading, setExclusionLoading] = useState(false);
  const [exportDrawerOpen, setExportDrawerOpen] = useState(false);
  const [exportDistributor, setExportDistributor] = useState<AdminDistributor | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const loadExclusions = useCallback(async (distributorId: string) => {
    setExclusionDistributorId(distributorId);
    setExclusionLoading(true);
    setExclusionDrawerOpen(true);
    const res = await getAdminSubcategoryExclusions(distributorId);
    if (res.data) setExclusionData(res.data);
    setExclusionLoading(false);
  }, []);

  const handleExport = async (type: 'catalog' | 'quotes' | 'reps') => {
    if (!exportDistributor) return;
    setExporting(type);
    setExportError(null);
    try {
      await downloadDistributorExport(exportDistributor.id, type);
    } catch (e: any) {
      setExportError(e.message || 'Download failed');
    }
    setExporting(null);
  };

  const saveExclusions = useCallback(async (actions: { confirm?: string[]; exclude?: string[]; include?: string[]; confirm_all?: boolean }) => {
    if (!exclusionDistributorId) return;
    const res = await updateAdminSubcategoryExclusions(exclusionDistributorId, actions);
    if (res.data) setExclusionData(res.data);
  }, [exclusionDistributorId]);

  useEffect(() => {
    loadDistributors();
  }, []);

  async function loadDistributors() {
    setLoading(true);
    const res = await getAdminDistributors();
    if (res.data) setDistributors(res.data);
    else setError(res.error || 'Failed to load');
    setLoading(false);
  }

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortField(field);
      setSortDir(field === 'created_at' ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="text-gray-300" />;
    return sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const filtered = useMemo(() => {
    let result = [...distributors];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (d) =>
          (d.name || '').toLowerCase().includes(q) ||
          (d.email_domain || '').toLowerCase().includes(q) ||
          (d.region || '').toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name': cmp = (a.name || '').localeCompare(b.name || ''); break;
        case 'region': cmp = (a.region || '').localeCompare(b.region || ''); break;
        case 'status': cmp = (a.status || '').localeCompare(b.status || ''); break;
        case 'rep_count': cmp = (a.rep_count || 0) - (b.rep_count || 0); break;
        case 'product_count': cmp = (a.product_count || 0) - (b.product_count || 0); break;
        case 'categorization_pct': cmp = (a.categorization_pct ?? -1) - (b.categorization_pct ?? -1); break;
        case 'created_at': cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime(); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [distributors, search, sortField, sortDir]);

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    const res = await createDistributor({
      name: newName.trim(),
      email_domain: newDomain.trim() || undefined,
      region: newRegion.trim() || undefined,
      admin_email: newAdminEmail.trim() || undefined,
      admin_first_name: newAdminFirstName.trim() || undefined,
      admin_last_name: newAdminLastName.trim() || undefined,
    });
    setCreating(false);
    if (res.data) {
      setShowCreate(false);
      setNewName('');
      setNewDomain('');
      setNewRegion('');
      setNewAdminEmail('');
      setNewAdminFirstName('');
      setNewAdminLastName('');
      loadDistributors();
    } else {
      setError(res.error || 'Failed to create distributor');
    }
  }

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className={ADMIN_PAGE_FRAME} style={ADMIN_PAGE_FRAME_STYLE}>
      <div className="flex items-center justify-between mb-6">
        <h1
          className="text-2xl font-bold text-[#2A2A2A]"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Distributors
        </h1>
        <Button
          onClick={() => setShowCreate(!showCreate)}
          className="bg-[#7FAEC2] hover:bg-[#6A9AB0] text-white"
        >
          <Plus size={16} className="mr-1" /> Create Distributor
        </Button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
          <h3 className="font-medium text-[#2A2A2A] mb-3">New Distributor</h3>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-gray-500 mb-1 block">Name *</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Sysco, US Foods..." />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs text-gray-500 mb-1 block">Email Domain</label>
              <Input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="sysco.com" />
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="text-xs text-gray-500 mb-1 block">Region</label>
              <Input value={newRegion} onChange={(e) => setNewRegion(e.target.value)} placeholder="Northeast" />
            </div>
            <div className="w-full pt-2">
              <p className="text-xs text-gray-400 mb-2">Distributor Admin (optional)</p>
              <div className="flex flex-wrap gap-3">
                <div className="flex-1 min-w-[180px]">
                  <label className="text-xs text-gray-500 mb-1 block">Admin Email</label>
                  <Input value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} placeholder="admin@distributor.com" />
                </div>
                <div className="flex-1 min-w-[120px]">
                  <label className="text-xs text-gray-500 mb-1 block">First Name</label>
                  <Input value={newAdminFirstName} onChange={(e) => setNewAdminFirstName(e.target.value)} placeholder="First" />
                </div>
                <div className="flex-1 min-w-[120px]">
                  <label className="text-xs text-gray-500 mb-1 block">Last Name</label>
                  <Input value={newAdminLastName} onChange={(e) => setNewAdminLastName(e.target.value)} placeholder="Last" />
                </div>
              </div>
            </div>
            <Button onClick={handleCreate} disabled={creating || !newName.trim()} className="bg-[#7FAEC2] hover:bg-[#6A9AB0] text-white">
              {creating ? 'Creating...' : 'Create'}
            </Button>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search distributors..." className="pl-9" />
        </div>
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} distributors</span>
      </div>

      {loading && <p className="text-sm text-gray-400 py-8">Loading distributors...</p>}
      {error && <p className="text-sm text-red-500 py-8">{error}</p>}

      {!loading && !error && filtered.length === 0 && <AdminEmptyState label="distributors" />}

      {!loading && filtered.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="cursor-pointer" onClick={() => toggleSort('name')}>
                    <div className="flex items-center gap-1">Name <SortIcon field="name" /></div>
                  </TableHead>
                  <TableHead>Email Domain</TableHead>
                  <TableHead className="cursor-pointer" onClick={() => toggleSort('region')}>
                    <div className="flex items-center gap-1">Region <SortIcon field="region" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer text-right" onClick={() => toggleSort('rep_count')}>
                    <div className="flex items-center justify-end gap-1">Reps <SortIcon field="rep_count" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer text-right" onClick={() => toggleSort('product_count')}>
                    <div className="flex items-center justify-end gap-1">Products <SortIcon field="product_count" /></div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-right"
                    onClick={() => toggleSort('categorization_pct')}
                    title="% of active products the classifier placed into a specific category (not 'Other'/unclassified). Not a measure of whether a raw category value is present."
                  >
                    <div className="flex items-center justify-end gap-1">Classification Confidence <SortIcon field="categorization_pct" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => toggleSort('status')}>
                    <div className="flex items-center gap-1">Status <SortIcon field="status" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => toggleSort('created_at')}>
                    <div className="flex items-center gap-1">Created <SortIcon field="created_at" /></div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((d) => (
                  <TableRow key={d.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Link to={`/qm-admin/distributors/${d.id}`} className="font-medium text-[#7FAEC2] hover:underline">
                          {d.name}
                        </Link>
                        {d.unclaimed && (
                          <span style={{
                            fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
                            padding: '2px 8px', borderRadius: 999, background: '#F3F4F6', color: '#6B7280',
                            border: '1px solid #E5E7EB'
                          }}>Unclaimed</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">{d.email_domain || 'None'}</TableCell>
                    <TableCell className="text-sm text-gray-500">{d.region || 'None'}</TableCell>
                    <TableCell className="text-sm text-right">{d.rep_count ?? 0}</TableCell>
                    <TableCell className="text-sm text-right">{d.product_count ?? 0}</TableCell>
                    <TableCell className="text-sm text-right">
                      {d.categorization_pct == null ? (
                        <span className="text-gray-400">-</span>
                      ) : (
                        <span className={d.categorization_pct < 50 ? 'text-amber-600' : 'text-gray-700'}>
                          {d.categorization_pct}%
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${d.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {d.status || 'unknown'}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">{formatDate(d.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => { setExportDistributor(d); setExportDrawerOpen(true); setExportError(null); }}
                          className="text-xs px-1.5 py-1 border border-gray-300 rounded hover:bg-gray-50"
                          title="Download exports"
                        >
                          <Download size={13} />
                        </button>
                        <button
                          onClick={() => loadExclusions(d.id)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                          aria-label={`Subcategory exclusions for ${d.name}`}
                        >
                          Subcategory Exclusions
                        </button>
                        {d.admin_user_id ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-[#7FAEC2] hover:text-[#6A9AB0]"
                            disabled={impersonating === d.admin_user_id}
                            onClick={() => handleImpersonate(d.admin_user_id!, d.admin_user_name || d.name, setImpersonating, setError)}
                            aria-label={`Sign in as ${d.admin_user_name || d.name}`}
                            title={`Sign in as ${d.admin_user_name || d.name}`}
                          >
                            <UserCheck size={14} className="mr-1" />
                            {impersonating === d.admin_user_id ? 'Switching...' : 'Impersonate'}
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">No user</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <SubcategoryExclusionDrawer
        isOpen={exclusionDrawerOpen}
        onClose={() => { setExclusionDrawerOpen(false); setExclusionDistributorId(null); }}
        data={exclusionData}
        onSave={saveExclusions}
        loading={exclusionLoading}
      />

      {/* Export Drawer */}
      <Sheet open={exportDrawerOpen} onOpenChange={setExportDrawerOpen}>
        <SheetContent side="right">
          <SheetHeader>
            <SheetTitle style={{ fontFamily: "'Playfair Display', serif" }}>
              Export: {exportDistributor?.name}
            </SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-3 px-1">
            <p className="text-sm text-gray-500">Download an Excel file for this distributor.</p>
            {exportError && <p className="text-sm text-red-500">{exportError}</p>}
            <button
              onClick={() => handleExport('catalog')}
              disabled={!!exporting}
              className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 text-left"
            >
              <Download size={16} className="text-[#7FAEC2] shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#2A2A2A]">Catalog</p>
                <p className="text-xs text-gray-400">{exportDistributor?.product_count ?? 0} products</p>
              </div>
              {exporting === 'catalog' && <span className="ml-auto text-xs text-gray-400">Downloading...</span>}
            </button>
            <button
              onClick={() => handleExport('quotes')}
              disabled={!!exporting}
              className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 text-left"
            >
              <Download size={16} className="text-[#7FAEC2] shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#2A2A2A]">Quotes</p>
                <p className="text-xs text-gray-400">All quote names & details</p>
              </div>
              {exporting === 'quotes' && <span className="ml-auto text-xs text-gray-400">Downloading...</span>}
            </button>
            <button
              onClick={() => handleExport('reps')}
              disabled={!!exporting}
              className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 text-left"
            >
              <Download size={16} className="text-[#7FAEC2] shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#2A2A2A]">Reps</p>
                <p className="text-xs text-gray-400">{exportDistributor?.rep_count ?? 0} reps</p>
              </div>
              {exporting === 'reps' && <span className="ml-auto text-xs text-gray-400">Downloading...</span>}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
